/**
 * Final Peril correct-response reveal band (#205, FR-6.6) — presentational.
 *
 * Rendered on the Player + Presentation surfaces throughout Final Peril judging
 * (`FINAL_JUDGING`, while contestants are walked — DI-A(b)) and the post-judging
 * linger (`FINAL_REVEAL`). Reuses the #192 amber `.clue-reveal` visual language,
 * specialized for Final: the "Final Peril — {category}" header, "The correct
 * response" label, and — only during the linger, once `resolvesAtEpochMs` is set —
 * a "Final results in {n}s" countdown toward GAME_OVER. Copy locked by D205.10.
 *
 * The server reveals the response to all roles only during these phases
 * (game-server `views.ts`); this component just displays what state carries.
 */

import { useEffect, useState } from 'react';
import { FINAL_PERIL } from './brand';

export interface FinalRevealProps {
  /** The correct response — server-revealed during FINAL_JUDGING/FINAL_REVEAL. */
  response: string;
  category?: string;
  /**
   * Epoch ms at which the linger ends and the game advances to GAME_OVER. Set only
   * during `FINAL_REVEAL`; when present a "Final results in {n}s" countdown shows.
   */
  resolvesAtEpochMs?: number | null;
  /** Injectable clock for tests (defaults to `Date.now`). */
  now?: () => number;
}

/** Whole seconds remaining until the game advances (never negative). */
function secondsLeft(resolvesAtEpochMs: number, now: () => number): number {
  return Math.max(0, Math.ceil((resolvesAtEpochMs - now()) / 1000));
}

export function FinalReveal({
  response,
  category,
  resolvesAtEpochMs,
  now = Date.now,
}: FinalRevealProps): React.JSX.Element {
  const [remaining, setRemaining] = useState(() =>
    resolvesAtEpochMs != null ? secondsLeft(resolvesAtEpochMs, now) : 0,
  );

  useEffect(() => {
    if (resolvesAtEpochMs == null) return;
    setRemaining(secondsLeft(resolvesAtEpochMs, now));
    const id = setInterval(() => {
      setRemaining(secondsLeft(resolvesAtEpochMs, now));
    }, 250);
    return () => clearInterval(id);
  }, [resolvesAtEpochMs, now]);

  return (
    <div
      className="clue-panel clue-reveal clue-reveal--unanswered"
      data-testid="final-reveal-band"
    >
      <div className="clue-panel__value">
        {FINAL_PERIL}
        {category ? ` — ${category}` : ''}
      </div>
      <div className="clue-reveal__response" data-testid="final-reveal-response">
        <div className="clue-reveal__response-label">The correct response</div>
        {response}
      </div>
      {resolvesAtEpochMs != null && (
        <div className="clue-reveal__countdown" data-testid="final-reveal-countdown">
          Final results in {remaining}s
        </div>
      )}
    </div>
  );
}
